import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import ShopPosterDownloadSection from '../components/poster/ShopPosterDownloadSection';
import {
  createShopKioskDomain,
  fetchShopKioskDomain,
  patchShopKioskDomain,
  setShopKioskPrimary,
} from '../services/api';
import type { ShopKioskDomainPayload } from '../types';

function subdomainLabelFromFqdn(fqdn: string, base: string): string {
  const suffix = `.${base}`;
  if (base && fqdn.endsWith(suffix)) {
    return fqdn.slice(0, -suffix.length);
  }
  const parts = fqdn.split('.');
  return parts[0] || '';
}

export default function KioskDomainPage() {
  const [payload, setPayload] = useState<ShopKioskDomainPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  /** When shop already has a kiosk domain: false = read-only summary, true = change subdomain form */
  const [isEditing, setIsEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchShopKioskDomain();
      if (res.success && res.data) {
        setPayload(res.data);
        if (res.data.subdomain) {
          setLabel(res.data.subdomain);
        } else if (res.data.domain?.domain) {
          setLabel(subdomainLabelFromFqdn(res.data.domain.domain, res.data.kiosk_base_domain));
        } else {
          setLabel('');
        }
      } else {
        toast.error(res.message || 'Could not load kiosk URL');
      }
    } catch {
      toast.error('Could not load kiosk URL');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const bases = payload?.kiosk_base_domains?.length
    ? payload.kiosk_base_domains
    : payload?.kiosk_base_domain
      ? [payload.kiosk_base_domain]
      : [];
  const base = bases[0] ?? '';
  const previews = bases.map((suffix) => (label.trim() ? `${label.trim().toLowerCase()}.${suffix}` : `….${suffix}`));

  const resetLabelFromPayload = useCallback(() => {
    if (payload?.subdomain) {
      setLabel(payload.subdomain);
    } else if (payload?.domain?.domain && base) {
      setLabel(subdomainLabelFromFqdn(payload.domain.domain, base));
    }
  }, [payload?.subdomain, payload?.domain?.domain, base]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await createShopKioskDomain(label.trim());
      if (res.success && res.data) {
        toast.success('Kiosk URL created');
        await load();
      } else {
        toast.error(res.message || 'Could not create');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Could not create');
    } finally {
      setSaving(false);
    }
  };

  const savedHosts = payload?.domains?.length
    ? payload.domains
    : payload?.domain
      ? [payload.domain]
      : [];

  const chooseMain = async (domainId: string) => {
    setSaving(true);
    try {
      const res = await setShopKioskPrimary(domainId);
      if (res.success) {
        toast.success('Main URL updated. Emails and posters will use it.');
        await load();
      } else {
        toast.error(res.message || 'Could not set the main URL');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Could not set the main URL');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await patchShopKioskDomain(label.trim());
      if (res.success && res.data) {
        toast.success('Kiosk URL updated');
        setIsEditing(false);
        await load();
      } else {
        toast.error(res.message || 'Could not update');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || 'Could not update');
    } finally {
      setSaving(false);
    }
  };

  const hasDomain = !!payload?.domain;

  const kioskPublicUrl =
    hasDomain && payload?.domain?.domain
      ? `https://${payload.domain.domain.replace(/^https?:\/\//i, '')}`
      : null;

  return (
    <AppLayout contentFullWidth>
      <h1 className="text-xl font-semibold tracking-tight text-stone-900 mb-2">Kiosk URL</h1>
      <p className="text-sm text-stone-600 mb-6 max-w-xl">
        Enter one subdomain label. It is created on every shop base domain, and players who register on any of
        those URLs belong to this shop. Choose one hostname as the main URL for credential emails and posters.
      </p>
      {loading ? (
        <PageLoadingIndicator variant="page" accentColor="#059669" />
      ) : !base ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900 max-w-xl">
          No shop base domain is registered yet. Ask an admin to add one.
        </div>
      ) : (
        <div className="w-full max-w-lg space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
          <div>
            <p className="text-xs font-medium uppercase text-stone-500">Preview</p>
            <ul className="mt-1 space-y-1">
              {previews.map((host) => (
                <li key={host} className="font-mono text-sm text-stone-900 break-all">
                  https://{host}
                </li>
              ))}
            </ul>
          </div>
          {!hasDomain ? (
            <form onSubmit={handleCreate} className="space-y-3">
              <label className="block text-sm font-medium text-stone-800" htmlFor="kiosk-sub-new">
                Subdomain label
              </label>
              <input
                id="kiosk-sub-new"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. mystore"
                autoComplete="off"
              />
              <p className="text-xs text-stone-500">
                Lowercase letters, numbers, and hyphens between segments. One label is used on every base domain.
              </p>
              <button
                type="submit"
                disabled={saving || !label.trim()}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create kiosk URL'}
              </button>
            </form>
          ) : isEditing ? (
            <form onSubmit={handleUpdate} className="space-y-3">
              <label className="block text-sm font-medium text-stone-800" htmlFor="kiosk-sub-edit">
                Change subdomain
              </label>
              <input
                id="kiosk-sub-edit"
                className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="new label"
                autoComplete="off"
              />
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-md p-2">
                Changing the URL may break old bookmarks until DNS updates propagate.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    resetLabelFromPayload();
                    setIsEditing(false);
                  }}
                  className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !label.trim()}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save new label'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              {savedHosts.length > 0 ? (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium text-stone-800">Main URL</legend>
                  <p className="text-xs text-stone-500">
                    Credential emails and the shop poster use this hostname.
                  </p>
                  {savedHosts.map((host) => {
                    const mainId = savedHosts.find((row) => row.isPrimary)?.id || payload?.domain?.id;
                    return (
                      <label key={host.id} className="flex items-start gap-2 text-sm text-stone-800">
                        <input
                          type="radio"
                          name="kiosk-main"
                          className="mt-1"
                          checked={host.id === mainId}
                          disabled={saving}
                          onChange={() => {
                            if (host.id !== mainId) void chooseMain(host.id);
                          }}
                        />
                        <span className="font-mono break-all">https://{host.domain}</span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-800 shadow-sm hover:bg-stone-50"
              >
                <Pencil className="h-4 w-4 text-stone-600" aria-hidden />
                Edit
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && base && hasDomain && kioskPublicUrl ? (
        <ShopPosterDownloadSection kioskUrl={kioskPublicUrl} />
      ) : null}
    </AppLayout>
  );
}
